/**
 * Vínculo da O.S. do SIAG com a O.S. sincronizada do ERP.
 *
 * O SIAG SUGERE (placa + data + KM/valor/fornecedor, com a explicação de cada
 * ponto) e quem vincula é a pessoa. Também dá para buscar à mão pelo número.
 * Desvincular pede o motivo e fica no histórico.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MaintenanceErpCandidateDto, MaintenanceErpOrderListDto, MaintenanceWorkOrderDetailDto, MaintenanceWorkOrderErpLinkDto,
} from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtBRL, fmtNumber } from "../../lib/format";
import { fmtDay } from "./preventive-ui";

const NIVEL = { alta: ["badge-green", "Correspondência alta"], possivel: ["badge-amber", "Possível correspondência"], fraca: ["badge-gray", "Correspondência fraca"] } as const;
const fmtDT = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

export function WorkOrderErpLink({
  o, pode, onMudou,
}: { o: MaintenanceWorkOrderDetailDto; pode: boolean; onMudou: () => void }) {
  const ativos = o.erpLinks.filter((l) => l.status === "active");
  const desfeitos = o.erpLinks.filter((l) => l.status === "unlinked");
  const [buscando, setBuscando] = useState(false);
  const [janela, setJanela] = useState(3);
  const [fracas, setFracas] = useState(false);
  const [numero, setNumero] = useState("");
  const [busca, setBusca] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [desvinculando, setDesvinculando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");

  const candidatos = useQuery({
    queryKey: ["maintenance", "erp-candidates", o.id, janela, fracas],
    queryFn: () => api.get<{ windowDays: number; candidates: MaintenanceErpCandidateDto[] }>(
      `/maintenance/work-orders/${o.id}/erp-candidates?windowDays=${janela}${fracas ? "&includeWeak=1" : ""}`),
    enabled: buscando && pode,
  });
  const manual = useQuery({
    queryKey: ["maintenance", "erp-orders", "manual", o.vehiclePlate, busca],
    queryFn: () => api.get<MaintenanceErpOrderListDto>(
      `/maintenance/erp-orders?plate=${encodeURIComponent(o.vehiclePlate)}&q=${encodeURIComponent(busca)}&limit=20`),
    enabled: buscando && pode && busca.trim() !== "",
  });

  const falha = (e: unknown) => setErro(e instanceof ApiError ? e.message : "Não foi possível concluir.");
  const vincular = useMutation({
    mutationFn: (erpOrderId: string) => api.post(`/maintenance/work-orders/${o.id}/erp-links`, { erpOrderId }),
    onSuccess: () => { setErro(null); setBuscando(false); onMudou(); },
    onError: falha,
  });
  const desvincular = useMutation({
    mutationFn: (linkId: string) => api.post(`/maintenance/work-orders/${o.id}/erp-links/${linkId}/unlink`, { reason: motivo.trim() }),
    onSuccess: () => { setErro(null); setDesvinculando(null); setMotivo(""); onMudou(); },
    onError: falha,
  });

  return (
    <section className="card p-4 space-y-3">
      <h3 className="font-medium text-gray-900">Vínculo com a O.S. do ERP</h3>
      {erro && <p className="rounded-lg border border-danger-600/30 bg-danger-50 p-2 text-sm text-danger-600">{erro}</p>}

      {ativos.map((l) => (
        <VinculoAtivo key={l.id} l={l} pode={pode}
          desvinculando={desvinculando === l.id}
          onDesvincular={() => { setDesvinculando(l.id); setMotivo(""); }}
          motivo={motivo} onMotivo={setMotivo} pendente={desvincular.isPending}
          onConfirmar={() => desvincular.mutate(l.id)} onCancelar={() => setDesvinculando(null)} />
      ))}

      {ativos.length === 0 && !buscando && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">Nenhuma O.S. do ERP vinculada.</p>
          {pode && <button className="btn-secondary" onClick={() => setBuscando(true)}>Buscar correspondência</button>}
        </div>
      )}
      {ativos.length > 0 && !buscando && pode && (
        <button className="btn-ghost text-sm" onClick={() => setBuscando(true)}>Vincular outra O.S. do ERP</button>
      )}

      {buscando && pode && (
        <div className="space-y-3 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            <label className="flex items-center gap-1">Janela de datas
              <select className="input py-0.5" value={janela} onChange={(e) => setJanela(Number(e.target.value))}>
                {[1, 3, 7, 15].map((d) => <option key={d} value={d}>±{d} dia{d > 1 ? "s" : ""}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={fracas} onChange={(e) => setFracas(e.target.checked)} /> Incluir correspondências fracas</label>
            <button className="btn-ghost ml-auto text-sm" onClick={() => setBuscando(false)}>Fechar</button>
          </div>

          {candidatos.isLoading && <p className="text-sm text-gray-500">Procurando…</p>}
          {candidatos.data && candidatos.data.candidates.length === 0 && (
            <p className="text-sm text-gray-500">Nenhuma O.S. do ERP desta placa com correspondência suficiente. Tente ampliar a janela ou buscar pelo número.</p>
          )}
          <ul className="space-y-2">
            {candidatos.data?.candidates.map((c) => (
              <Candidato key={c.id} c={c} pendente={vincular.isPending} onVincular={() => vincular.mutate(c.id)} />
            ))}
          </ul>

          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setBusca(numero.trim()); }}>
            <input className="input flex-1" placeholder="Buscar pelo número da O.S. do ERP" value={numero} onChange={(e) => setNumero(e.target.value)} />
            <button className="btn-secondary" disabled={!numero.trim()}>Buscar</button>
          </form>
          {manual.data && manual.data.rows.length === 0 && <p className="text-sm text-gray-500">Nenhuma O.S. desta placa com esse número.</p>}
          <ul className="space-y-2">
            {manual.data?.rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-surface-border p-2 text-sm">
                <span>
                  <span className="font-medium">O.S. ERP {r.number}</span>
                  <span className="block text-xs text-gray-500">{fmtDay(r.openedAt)} · {r.odometer != null ? `${fmtNumber(r.odometer)} km` : "sem KM"} · {fmtBRL(r.total)}</span>
                </span>
                <button className="btn-secondary" disabled={vincular.isPending} onClick={() => vincular.mutate(r.id)}>Vincular</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {vinculosDesfeitos(desfeitos)}
    </section>
  );
}

function vinculosDesfeitos(desfeitos: MaintenanceWorkOrderErpLinkDto[]) {
  if (!desfeitos.length) return null;
  return (
    <details className="border-t border-gray-100 pt-2 text-sm">
      <summary className="cursor-pointer text-gray-500">Vínculos desfeitos ({desfeitos.length})</summary>
      <ul className="mt-2 space-y-1.5">
        {desfeitos.map((l) => (
          <li key={l.id} className="text-xs text-gray-500">
            O.S. ERP {l.erpOrder?.number ?? l.erpExternalKey} desvinculada por {l.unlinkedByName ?? "—"} em {fmtDT(l.unlinkedAt)}. Motivo: {l.unlinkReason}
          </li>
        ))}
      </ul>
    </details>
  );
}

function VinculoAtivo({
  l, pode, desvinculando, onDesvincular, motivo, onMotivo, pendente, onConfirmar, onCancelar,
}: {
  l: MaintenanceWorkOrderErpLinkDto; pode: boolean; desvinculando: boolean; onDesvincular: () => void;
  motivo: string; onMotivo: (v: string) => void; pendente: boolean; onConfirmar: () => void; onCancelar: () => void;
}) {
  const e = l.erpOrder;
  return (
    <div className="rounded-lg border border-tm-100 bg-tm-50/40 p-3 text-sm">
      <p className="font-medium text-gray-900">O.S. ERP #{e?.number ?? l.erpExternalKey}</p>
      {e && (
        <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5">
          <Par r="Data">{fmtDay(e.date)}</Par>
          <Par r="KM">{e.odometer != null ? fmtNumber(e.odometer) : "—"}</Par>
          <Par r="Fornecedor">{e.supplierName ?? (e.origin === "internal" ? "Oficina própria" : "—")}</Par>
          <Par r="Valor">{fmtBRL(e.total)}</Par>
        </dl>
      )}
      <p className="mt-2 text-xs text-gray-500">
        Vinculada por {l.linkedByName ?? "—"} em {fmtDT(l.linkedAt)}
        {l.matchScore != null ? ` · correspondência ${l.matchScore}%` : ""}
      </p>
      {l.matchReason && <p className="text-xs text-gray-400">{l.matchReason}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {e && <Link className="btn-secondary" to={`/frota/manutencao/erp/${e.id}`}>Ver O.S. ERP</Link>}
        {pode && !desvinculando && <button className="btn-ghost text-danger-600" onClick={onDesvincular}>Desvincular</button>}
      </div>
      {desvinculando && (
        <div className="mt-2 space-y-2">
          <input className="input w-full" autoFocus maxLength={500} value={motivo} onChange={(ev) => onMotivo(ev.target.value)} placeholder="Motivo do desvínculo (obrigatório)" />
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={onCancelar}>Voltar</button>
            <button className="btn-secondary border-danger-600/40 text-danger-600" disabled={pendente || !motivo.trim()} onClick={onConfirmar}>
              {pendente ? "Desvinculando…" : "Confirmar desvínculo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Candidato({ c, pendente, onVincular }: { c: MaintenanceErpCandidateDto; pendente: boolean; onVincular: () => void }) {
  const [badge, rotulo] = NIVEL[c.level];
  const ocupada = !!c.linkedToWorkOrderId;
  return (
    <li className="rounded-lg border border-surface-border p-2.5 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-gray-900">O.S. ERP {c.number}</p>
          <p className="text-xs text-gray-500">
            {fmtDay(c.date)} · <span className="font-mono">{c.plate}</span> · {c.odometer != null ? `${fmtNumber(c.odometer)} km` : "sem KM"} · {c.supplierName ?? (c.origin === "internal" ? "Oficina própria" : "—")} · {fmtBRL(c.total)}
          </p>
        </div>
        <span className={badge} title={rotulo}>{c.score}%</span>
      </div>
      <p className="mt-1 text-xs text-gray-500">{rotulo}: {c.reasons.join(" · ")}</p>
      <div className="mt-2">
        {ocupada
          ? <span className="text-xs text-warning-700">Já vinculada a outra O.S. do SIAG</span>
          : <button className="btn-secondary" disabled={pendente} onClick={onVincular}>Vincular</button>}
      </div>
    </li>
  );
}

const Par = ({ r, children }: { r: string; children: React.ReactNode }) => (
  <div><dt className="text-xs text-gray-500">{r}</dt><dd className="text-gray-800">{children}</dd></div>
);
